import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, MapPin, Check, Loader2 } from 'lucide-react';
import type { ResourceCategory } from '@/contexts/ResourceContext';
import { useResources } from '@/contexts/ResourceContext';

const categoryConfig: Record<ResourceCategory, { label: string; icon: string }> = {
  food: { label: 'Food & Groceries', icon: '🍎' },
  shelter: { label: 'Shelter & Housing', icon: '🏠' },
  medical: { label: 'Health Care', icon: '⚕️' },
  skills: { label: 'Skills & Learning', icon: '📚' },
  tools: { label: 'Tools & Equipment', icon: '🔧' },
  transport: { label: 'Transportation', icon: '🚗' },
  legal: { label: 'Legal Aid', icon: '⚖️' },
  childcare: { label: 'Childcare', icon: '👶' },
  elderlycare: { label: 'Elder Care', icon: '👵' },
  'mental-health': { label: 'Mental Health', icon: '💜' },
  community: { label: 'Community', icon: '🌱' },
  other: { label: 'Other', icon: '✨' },
};

interface AddResourceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddResourceDialog({ open, onOpenChange }: AddResourceDialogProps) {
  const { addResource } = useResources();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    category: '' as ResourceCategory | '',
    description: '',
    address: '',
    latitude: -34.8069,
    longitude: 173.9185,
    phone: '',
    email: '',
    website: '',
    hours: '',
    tags: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      addResource({
        name: formData.name,
        category: formData.category as ResourceCategory,
        description: formData.description,
        address: formData.address,
        latitude: formData.latitude,
        longitude: formData.longitude,
        contact: {
          phone: formData.phone || undefined,
          email: formData.email || undefined,
          website: formData.website || undefined,
        },
        hours: formData.hours || undefined,
        tags: formData.tags
          .split(',')
          .map((t) => t.trim().toLowerCase())
          .filter(Boolean),
      });

      // Reset form
      setFormData({
        name: '',
        category: '',
        description: '',
        address: '',
        latitude: -34.8069,
        longitude: -122.4194,
        phone: '',
        email: '',
        website: '',
        hours: '',
        tags: '',
      });

      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof typeof formData, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <Plus className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <DialogTitle>Add a Resource</DialogTitle>
              <DialogDescription>
                Share a mutual aid resource with your community
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Resource Name *</Label>
            <Input
              id="name"
              placeholder="e.g., Community Food Pantry"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              required
              className="h-11"
            />
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label htmlFor="category">Category *</Label>
            <Select
              value={formData.category}
              onValueChange={(value) => handleChange('category', value)}
              required
            >
              <SelectTrigger className="h-11">
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(categoryConfig).map(([key, config]) => (
                  <SelectItem key={key} value={key}>
                    <span className="mr-2">{config.icon}</span>
                    {config.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              placeholder="Describe what this resource offers and who it's for..."
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              required
              rows={3}
              className="resize-none"
            />
          </div>

          {/* Address */}
          <div className="space-y-2">
            <Label htmlFor="address">Address *</Label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="address"
                placeholder="123 Main St, City"
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                required
                className="pl-10 h-11"
              />
            </div>
          </div>

          {/* Location (simplified - would use geocoding in production) */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="latitude">Latitude</Label>
              <Input
                id="latitude"
                type="number"
                step="any"
                placeholder="-34.8069"
                value={formData.latitude}
                onChange={(e) => handleChange('latitude', parseFloat(e.target.value))}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="longitude">Longitude</Label>
              <Input
                id="longitude"
                type="number"
                step="any"
                placeholder="-122.4194"
                value={formData.longitude}
                onChange={(e) => handleChange('longitude', parseFloat(e.target.value))}
                className="h-11"
              />
            </div>
          </div>

          {/* Hours */}
          <div className="space-y-2">
            <Label htmlFor="hours">Hours</Label>
            <Input
              id="hours"
              placeholder="e.g., Mon-Fri 9am-5pm"
              value={formData.hours}
              onChange={(e) => handleChange('hours', e.target.value)}
              className="h-11"
            />
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="555-0123"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                className="h-11"
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="contact@example.org"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className="h-11"
              />
            </div>
          </div>

          {/* Website */}
          <div className="space-y-2">
            <Label htmlFor="website">Website</Label>
            <Input
              id="website"
              type="url"
              placeholder="https://example.org"
              value={formData.website}
              onChange={(e) => handleChange('website', e.target.value)}
              className="h-11"
            />
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <Label htmlFor="tags">Tags</Label>
            <Input
              id="tags"
              placeholder="free, no-id, fresh-produce (comma separated)"
              value={formData.tags}
              onChange={(e) => handleChange('tags', e.target.value)}
              className="h-11"
            />
            <p className="text-xs text-muted-foreground">
              Separate tags with commas to help others find this resource
            </p>
          </div>

          <DialogFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  Add Resource
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
